import { environment } from "@/config/env";
import { useEliteStructure } from "@/contexts/EliteTemporaryContextProvider";
import axios from "axios";
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

export interface FidelityTierPlan {
  id: string;
  name: string;
  color: string;
  presetKey?: string;
  minPoints: number;
  maxPoints?: number | null;
  benefits?: string;
}

export interface GuestAreaConfig {
  isEnabled: boolean;
  isOrganizationCentralized: boolean;
  structureId: number;
  structureName: string;
  organizationId: number;
  organizationName?: string;
  programName: string;
  pointsPerSpendUnit: number;
  spendUnitAmount: number;
  welcomeBonusPoints: number;
  countStaySpend: boolean;
  countExtraServicesSpend: boolean;
  tiers: FidelityTierPlan[];
}

export interface GuestReservationMovement {
  id: number;
  structureId: number;
  structureName: string;
  number?: string;
  arrival?: string;
  departure?: string;
  nights?: number;
  roomType?: string;
  treatment?: string;
  amount: number;
  status?: string;
  bookingDate?: string;
}

export interface GuestFinancialTransaction {
  id: number;
  structureId: number;
  structureName: string;
  reservationId: number;
  transactionNumber: number;
  transactionDate: string;
  amount: number;
  currency: string;
  description?: string;
  billNumber?: string;
}

export interface GuestOrderedServiceItemMovement {
  id: number;
  serviceId: number;
  serviceTitle: string;
  quantity: number;
  price: number;
  status: string;
  startDate?: string;
  endDate?: string;
}

export interface GuestOrderedServiceMovement {
  id: number;
  structureId: number;
  structureName: string;
  reservationId?: number;
  roomNumber?: string;
  verified: boolean;
  totalPrice: number;
  createdAt?: string;
  items: GuestOrderedServiceItemMovement[];
}

export interface GuestFidelityProfile {
  accountId: number;
  guestId: number;
  structureId: number;
  registrationStructureName: string;
  organizationId: number;
  organizationName?: string;
  isOrganizationCentralized: boolean;
  name: string;
  surname: string;
  email: string;
  phone?: string;
  cardCode: string;
  isActive: boolean;
  registeredAt: string;
  lastLoginAt?: string;
  programName: string;
  currentPoints: number;
  earnedFromSpendPoints: number;
  welcomeBonusPoints: number;
  manualPointsAdjustment: number;
  legacyPoints: number;
  totalStaySpend: number;
  totalExtraServicesSpend: number;
  totalSpend: number;
  fidelityType: string;
  fidelityColor: string;
  fidelityBenefits?: string;
  nextFidelityType?: string;
  nextFidelityColor?: string;
  nextTierMinPoints?: number;
  pointsToNextTier: number;
  progressPercentage: number;
  availableTiers: FidelityTierPlan[];
  reservations: GuestReservationMovement[];
  financialTransactions: GuestFinancialTransaction[];
  orderedServices: GuestOrderedServiceMovement[];
}

interface RegisterPayload {
  email: string;
  password: string;
  name: string;
  surname: string;
  phone?: string;
}

export interface AuthActionOutcome {
  requiresVerification: boolean;
  email?: string;
  structureId?: number;
  message?: string;
}

export interface VerifyEmailPayload {
  structureId?: number;
  email?: string;
  otpCode?: string;
  magicToken?: string;
}

interface GuestAuthContextValue {
  config: GuestAreaConfig | null;
  profile: GuestFidelityProfile | null;
  isLoading: boolean;
  isAuthModalOpen: boolean;
  authModalTab: "login" | "register";
  openAuthModal: (tab?: "login" | "register") => void;
  closeAuthModal: () => void;
  login: (email: string, password: string) => Promise<AuthActionOutcome>;
  register: (payload: RegisterPayload) => Promise<AuthActionOutcome>;
  verifyEmail: (payload: VerifyEmailPayload) => Promise<void>;
  resendVerification: (email: string, structureId?: number) => Promise<string>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
}

const GuestAuthContext = createContext<GuestAuthContextValue | null>(null);

function getTokenStorageKey(config: GuestAreaConfig | null, structureId?: number): string {
  if (config?.isOrganizationCentralized && config.organizationId > 0) {
    return `helite_guest_token_org_${config.organizationId}`;
  }
  const sid = config?.structureId || structureId || Number(localStorage.getItem("structureId") || 0);
  return `helite_guest_token_str_${sid}`;
}

export const GuestAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { structure } = useEliteStructure();
  const [config, setConfig] = useState<GuestAreaConfig | null>(null);
  const [profile, setProfile] = useState<GuestFidelityProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState<boolean>(false);
  const [authModalTab, setAuthModalTab] = useState<"login" | "register">("login");

  const resolvedStructureId =
    structure?.id || Number(localStorage.getItem("structureId") || 0) || 0;

  const fetchProfileWithToken = useCallback(
    async (token: string, sid: number, activeConfig: GuestAreaConfig | null) => {
      try {
        axios.defaults.baseURL = environment.ELITE_BASE_URI;
        const res = await axios.get<GuestFidelityProfile>(`guestarea/me?structureId=${sid}`, {
          headers: {
            "x-guest-token": token,
          },
        });
        setProfile(res.data);
      } catch {
        const key = getTokenStorageKey(activeConfig, sid);
        localStorage.removeItem(key);
        setProfile(null);
      }
    },
    []
  );

  useEffect(() => {
    if (!resolvedStructureId || resolvedStructureId <= 0) {
      setConfig(null);
      setProfile(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    axios.defaults.baseURL = environment.ELITE_BASE_URI;

    axios
      .get<GuestAreaConfig>(`guestarea/config/${resolvedStructureId}`)
      .then(async (res) => {
        if (cancelled) return;
        const cfg = res.data;
        setConfig(cfg);

        if (!cfg?.isEnabled) {
          setProfile(null);
          return;
        }

        const storageKey = getTokenStorageKey(cfg, resolvedStructureId);
        const savedToken =
          localStorage.getItem(storageKey) ||
          localStorage.getItem(`helite_guest_token_str_${resolvedStructureId}`);

        if (savedToken) {
          await fetchProfileWithToken(savedToken, resolvedStructureId, cfg);
        } else {
          setProfile(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setConfig(null);
          setProfile(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedStructureId, fetchProfileWithToken]);

  const openAuthModal = useCallback((tab: "login" | "register" = "login") => {
    setAuthModalTab(tab);
    setIsAuthModalOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setIsAuthModalOpen(false);
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<AuthActionOutcome> => {
      if (!resolvedStructureId) throw new Error("Struttura non selezionata");
      axios.defaults.baseURL = environment.ELITE_BASE_URI;
      const res = await axios.post<{
        token?: string;
        profile?: GuestFidelityProfile;
        requiresVerification?: boolean;
        email?: string;
        structureId?: number;
        message?: string;
      }>("guestarea/login", {
        structureId: resolvedStructureId,
        email,
        password,
        clientBaseUrl: window.location.origin,
      });

      if (res.data.requiresVerification) {
        return {
          requiresVerification: true,
          email: res.data.email || email,
          structureId: res.data.structureId || resolvedStructureId,
          message: res.data.message,
        };
      }

      if (res.data.token && res.data.profile) {
        const key = getTokenStorageKey(config, resolvedStructureId);
        localStorage.setItem(key, res.data.token);
        setProfile(res.data.profile);
        setIsAuthModalOpen(false);
      }

      return { requiresVerification: false };
    },
    [resolvedStructureId, config]
  );

  const register = useCallback(
    async (payload: RegisterPayload): Promise<AuthActionOutcome> => {
      if (!resolvedStructureId) throw new Error("Struttura non selezionata");
      axios.defaults.baseURL = environment.ELITE_BASE_URI;
      const reservationToken = sessionStorage.getItem("webcheckin_token") || undefined;

      const res = await axios.post<{
        token?: string;
        profile?: GuestFidelityProfile;
        requiresVerification?: boolean;
        email?: string;
        structureId?: number;
        message?: string;
      }>("guestarea/register", {
        structureId: resolvedStructureId,
        ...payload,
        reservationToken,
        clientBaseUrl: window.location.origin,
      });

      if (res.data.requiresVerification) {
        return {
          requiresVerification: true,
          email: res.data.email || payload.email,
          structureId: res.data.structureId || resolvedStructureId,
          message: res.data.message,
        };
      }

      if (res.data.token && res.data.profile) {
        const key = getTokenStorageKey(config, resolvedStructureId);
        localStorage.setItem(key, res.data.token);
        setProfile(res.data.profile);
        setIsAuthModalOpen(false);
      }

      return { requiresVerification: false };
    },
    [resolvedStructureId, config]
  );

  const verifyEmail = useCallback(
    async (payload: VerifyEmailPayload) => {
      const targetStructureId = payload.structureId || resolvedStructureId;
      axios.defaults.baseURL = environment.ELITE_BASE_URI;

      const res = await axios.post<{ token: string; profile: GuestFidelityProfile }>(
        "guestarea/verify-email",
        {
          structureId: targetStructureId,
          email: payload.email,
          otpCode: payload.otpCode,
          magicToken: payload.magicToken,
        }
      );

      const effectiveSid = targetStructureId || res.data.profile.structureId;
      if (effectiveSid > 0 && !localStorage.getItem("structureId")) {
        localStorage.setItem("structureId", String(effectiveSid));
      }

      let activeCfg = config;
      if (!activeCfg && effectiveSid > 0) {
        try {
          const cfgRes = await axios.get<GuestAreaConfig>(`guestarea/config/${effectiveSid}`);
          activeCfg = cfgRes.data;
          setConfig(activeCfg);
        } catch {
          // ignore
        }
      }

      const key = getTokenStorageKey(activeCfg, effectiveSid);
      localStorage.setItem(key, res.data.token);
      localStorage.setItem(`helite_guest_token_str_${effectiveSid}`, res.data.token);
      setProfile(res.data.profile);
      setIsAuthModalOpen(false);
    },
    [resolvedStructureId, config]
  );

  const resendVerification = useCallback(
    async (email: string, structureId?: number): Promise<string> => {
      const targetStructureId = structureId || resolvedStructureId;
      axios.defaults.baseURL = environment.ELITE_BASE_URI;
      const res = await axios.post<{ message?: string }>("guestarea/resend-verification", {
        structureId: targetStructureId,
        email,
        clientBaseUrl: window.location.origin,
      });
      return (
        res.data?.message ||
        "Abbiamo inviato nuovamente il codice OTP e il link di conferma alla tua email."
      );
    },
    [resolvedStructureId]
  );

  const logout = useCallback(() => {
    const key = getTokenStorageKey(config, resolvedStructureId);
    localStorage.removeItem(key);
    localStorage.removeItem(`helite_guest_token_str_${resolvedStructureId}`);
    setProfile(null);
  }, [config, resolvedStructureId]);

  const refreshProfile = useCallback(async () => {
    if (!resolvedStructureId || !config?.isEnabled) return;
    const key = getTokenStorageKey(config, resolvedStructureId);
    const savedToken =
      localStorage.getItem(key) ||
      localStorage.getItem(`helite_guest_token_str_${resolvedStructureId}`);
    if (savedToken) {
      await fetchProfileWithToken(savedToken, resolvedStructureId, config);
    }
  }, [resolvedStructureId, config, fetchProfileWithToken]);

  return (
    <GuestAuthContext.Provider
      value={{
        config,
        profile,
        isLoading,
        isAuthModalOpen,
        authModalTab,
        openAuthModal,
        closeAuthModal,
        login,
        register,
        verifyEmail,
        resendVerification,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </GuestAuthContext.Provider>
  );
};

export function useGuestAuth() {
  const ctx = useContext(GuestAuthContext);
  if (!ctx) {
    throw new Error("useGuestAuth must be used within a GuestAuthProvider");
  }
  return ctx;
}
